import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router-dom';
import AppShell, {PublicShell} from './components/AppShell';
import ErrorBoundary from './components/feedback/ErrorBoundary';
import UploadQueueBridge from './components/UploadQueueBridge';
import {useAuth, useMe} from './hooks/useAuth';
import {BusyOverlay} from './hooks/useBusy';
import {ConfirmHost} from './hooks/useConfirm';
import {ToastViewport} from './hooks/useToast';
import AdminUsersPage from './pages/AdminUsersPage';
import EditTellPage from './pages/EditTellPage';
import ExportPage from './pages/ExportPage';
import GardenPage from './pages/GardenPage';
import AssistantsPage from './pages/AssistantsPage';
import JournalPage from './pages/JournalPage';
import MemoriesPage from './pages/MemoriesPage';
import MorePage from './pages/MorePage';
import {NewTellKindStep, NewTellPhotoStep, NewTellWordsStep} from './pages/NewTellPage';
import NotFoundPage from './pages/NotFoundPage';
import PasswordlessLoginPage from './pages/PasswordlessLoginPage';
import PlacePage from './pages/PlacePage';
import PlacesPage from './pages/PlacesPage';
import PlantPage from './pages/PlantPage';
import PlantsPage from './pages/PlantsPage';
import RecentlyDeletedPage from './pages/RecentlyDeletedPage';
import SignInPage from './pages/SignInPage';
import SpeciesPage from './pages/SpeciesPage';
import TodoFormPage from './pages/TodoFormPage';
import TodoPage from './pages/TodoPage';
import TellDetailPage from './pages/TellDetailPage';
import WelcomeInPage from './pages/WelcomeInPage';
import WelcomePage from './pages/WelcomePage';

/** Signed-in only. First-time users are sent to the welcome-in screen until they have set up. */
const RequireAuth = () => {
    const {isAuthenticated} = useAuth();
    const me = useMe();
    const {pathname, search} = useLocation();

    if (!isAuthenticated) {
        return <Navigate to="/signin" replace state={{from: `${pathname}${search}`}} />;
    }
    if (me.data?.needsSetup && pathname !== '/welcome') {
        return <Navigate to="/welcome" replace />;
    }
    return <Outlet />;
};

const RequireAdmin = () => {
    const {claims} = useAuth();
    return claims?.isAdmin ? <Outlet /> : <Navigate to="/journal" replace />;
};

/** The root: the landing page for visitors, the Journal for anyone signed in. */
const Home = () => {
    const {isAuthenticated} = useAuth();
    return isAuthenticated ? <Navigate to="/journal" replace /> : <WelcomePage />;
};

export default function App() {
    return (
        <ErrorBoundary>
            <AppRoutes />
            <UploadQueueBridge />
            <ToastViewport />
            <BusyOverlay />
            <ConfirmHost />
        </ErrorBoundary>
    );
}

function AppRoutes() {
    return (
        <Routes>
            <Route element={<PublicShell />}>
                <Route path="/" element={<Home />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/passwordless-login" element={<PasswordlessLoginPage />} />
                <Route element={<RequireAuth />}>
                    <Route path="/welcome" element={<WelcomeInPage />} />
                    <Route path="/tell/new" element={<NewTellPhotoStep />} />
                    <Route path="/tell/new/kind" element={<NewTellKindStep />} />
                    <Route path="/tell/new/words" element={<NewTellWordsStep />} />
                    <Route path="/tell/:id" element={<TellDetailPage />} />
                    <Route path="/tell/:id/edit" element={<EditTellPage />} />
                    <Route path="/plants/species/:key" element={<SpeciesPage />} />
                    <Route path="/plants/:id" element={<PlantPage />} />
                    <Route path="/places/:id" element={<PlacePage />} />
                    <Route path="/todo/new" element={<TodoFormPage />} />
                    <Route path="/todo/:id" element={<TodoFormPage />} />
                    <Route path="/memories" element={<MemoriesPage />} />
                </Route>
            </Route>
            <Route element={<RequireAuth />}>
                <Route element={<AppShell />}>
                    <Route path="/journal" element={<JournalPage />} />
                    <Route path="/garden" element={<GardenPage />} />
                    {/* The Map tab became Garden, and Seeds folded into Plants (24 Oct 2026). */}
                    <Route path="/map" element={<Navigate to="/garden" replace />} />
                    <Route path="/plants" element={<PlantsPage />} />
                    <Route path="/seeds" element={<Navigate to="/plants" replace />} />
                    <Route path="/todo" element={<TodoPage />} />
                    <Route path="/more" element={<MorePage />} />
                    <Route path="/places" element={<PlacesPage />} />
                    <Route path="/deleted" element={<RecentlyDeletedPage />} />
                    <Route path="/export" element={<ExportPage />} />
                    <Route path="/assistants" element={<AssistantsPage />} />
                    <Route element={<RequireAdmin />}>
                        <Route path="/admin/users" element={<AdminUsersPage />} />
                    </Route>
                </Route>
            </Route>
            <Route element={<PublicShell />}>
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
}
